import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import {
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  TextField,
  Autocomplete,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import { FadeLoader } from "react-spinners";
import ContractViewController from "../../redux/actions/ContractApiAction";
import EmployerViewController from "../../redux/actions/EmployerApiAction";

const contractTypeOptions = [
  { label: "Probation", value: "probation" },
  { label: "Permanent", value: "permanent" },
  { label: "Contract", value: "contract" },
  { label: "Consultant", value: "consultant" },
];

function ContractForm({ show, handleClose, initialData = null }) {
  const dispatch = useDispatch();
  const { addContract, updateContract, getContractTermsAndConditions } =
    ContractViewController();
  const { getAllEmployeeEmployer } = EmployerViewController();

  const { employers } = useSelector((state) => state.employer);
  const { isLoding, terms_conditions } = useSelector(
    (state) => state.contracts
  );

  const isEdit = Boolean(initialData?.id);

  const [initialValues, setInitialValues] = useState({
    employee: null,
    contract_type: "permanent",
    start_date: "",
    end_date: "",
    is_active: true,
    terms_and_conditions: [],
  });

  // Load employees
  useEffect(() => {
    if (show) {
      dispatch(getAllEmployeeEmployer());
    }
  }, [show, dispatch]);

  // Load terms when contract type changes
  useEffect(() => {
    if (show && initialValues.contract_type) {
      dispatch(getContractTermsAndConditions(initialValues.contract_type));
    }
  }, [show, initialValues.contract_type, dispatch]);

  // Fill data for edit
  useEffect(() => {
    if (show) {
      if (initialData) {
        setInitialValues({
          employee: initialData.employee || null,
          contract_type: initialData.contract_type || "permanent",
          start_date: initialData.start_date || "",
          end_date: initialData.end_date || "",
          is_active: initialData.is_active ?? true,
          terms_and_conditions: initialData.terms_and_conditions || [],
        });
      } else {
        setInitialValues({
          employee: null,
          contract_type: "permanent",
          start_date: "",
          end_date: "",
          is_active: true,
          terms_and_conditions: [],
        });
      }
    }
  }, [show, initialData]);

  const handleSubmit = (values, { resetForm }) => {
    const payload = {
      ...values,
      employee: values.employee?.id || values.employee,
    };

    const action = isEdit
      ? updateContract(initialData.id, payload)
      : addContract(payload);

    dispatch(action)
      .then(() => {
        resetForm();
        handleClose();
      })
      .catch((error) => console.error("Error submitting contract:", error));
  };

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      fullScreen
      sx={{
        height: "100%",
        width: "100%",
        maxWidth: "none",
        maxHeight: "none",
        margin: 0,
      }}
    >
      {/* Sticky Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "white",
          borderBottom: "1px solid #ddd",
        }}
      >
        {isEdit ? "Edit Contract" : "Add Contract"}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Scrollable Content */}
      <DialogContent
        dividers
        sx={{
          flex: 1,
          overflowY: "auto",
          maxHeight: "calc(100vh - 140px)", // leave room for header+footer
        }}
      >
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, handleChange, isSubmitting }) => (
            <Form id="contractForm">
              <Grid container spacing={2}>
                {/* Employee Autocomplete */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={employers || []}
                    getOptionLabel={(opt) =>
                      opt?.first_name && opt?.last_name
                        ? `${opt.first_name} ${opt.last_name} (${
                            opt.employee_id || ""
                          })`
                        : ""
                    }
                    value={
                      employers?.find(
                        (emp) =>
                          emp.id === (values.employee?.id || values.employee)
                      ) || null
                    }
                    onChange={(e, newValue) =>
                      setFieldValue("employee", newValue)
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Employee" />
                    )}
                  />
                </Grid>

                {/* Contract Type */}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={contractTypeOptions}
                    getOptionLabel={(opt) => opt.label}
                    value={
                      contractTypeOptions.find(
                        (opt) => opt.value === values.contract_type
                      ) || null
                    }
                    onChange={(e, newValue) => {
                      setFieldValue(
                        "contract_type",
                        newValue ? newValue.value : ""
                      );
                      setFieldValue("terms_and_conditions", []);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Contract Type" />
                    )}
                  />
                </Grid>

                {/* Dates */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    name="start_date"
                    value={values.start_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    name="end_date"
                    value={values.end_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Terms & Conditions */}
                <Grid item xs={12}>
                  {values.contract_type && (
                    <Box mt={2}>
                      <Typography variant="h6" gutterBottom>
                        Terms & Conditions
                      </Typography>

                      {(() => {
                        const selectedContract = terms_conditions.find(
                          (c) => c.contract_type === values.contract_type
                        );
                        const selectedTerms =
                          selectedContract?.terms_and_conditions || [];

                        const allChecked =
                          values.terms_and_conditions.length ===
                            selectedTerms.length && selectedTerms.length > 0;

                        return selectedTerms.length > 0 ? (
                          <Box>
                            <FormGroup>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={allChecked}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFieldValue(
                                          "terms_and_conditions",
                                          selectedTerms
                                        );
                                      } else {
                                        setFieldValue(
                                          "terms_and_conditions",
                                          []
                                        );
                                      }
                                    }}
                                  />
                                }
                                label="Accept All Terms & Conditions"
                              />
                            </FormGroup>

                            <FormGroup>
                              {selectedTerms.map((term, index) => {
                                const isChecked =
                                  values.terms_and_conditions.some(
                                    (t) =>
                                      t.title === term.title &&
                                      t.description === term.description
                                  );
                                return (
                                  <Box key={index} mb={1.5}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={isChecked}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setFieldValue(
                                                "terms_and_conditions",
                                                [
                                                  ...values.terms_and_conditions,
                                                  term,
                                                ]
                                              );
                                            } else {
                                              setFieldValue(
                                                "terms_and_conditions",
                                                values.terms_and_conditions.filter(
                                                  (t) =>
                                                    !(
                                                      t.title === term.title &&
                                                      t.description ===
                                                        term.description
                                                    )
                                                )
                                              );
                                            }
                                          }}
                                        />
                                      }
                                      label={
                                        <Typography
                                          variant="subtitle2"
                                          fontWeight="bold"
                                        >
                                          {term.title}
                                        </Typography>
                                      }
                                    />
                                    {term.description && (
                                      <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{ pl: 4, mt: -0.5 }}
                                      >
                                        {term.description}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              })}
                            </FormGroup>
                          </Box>
                        ) : (
                          <Typography>No terms available.</Typography>
                        );
                      })()}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>

      {/* Sticky Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          p: 2,
          position: "sticky",
          bottom: 0,
          backgroundColor: "white",
          borderTop: "1px solid #ddd",
          zIndex: 10,
        }}
      >
        <Button onClick={handleClose}>Cancel</Button>
        <Button type="submit" form="contractForm" variant="contained">
          {isEdit ? "Update" : "Submit"}
        </Button>
        {isLoding && (
          <FadeLoader
            loading={true}
            size={30}
            style={{ marginLeft: "10px" }}
          />
        )}
      </Box>
    </Dialog>
  );
}

export default ContractForm;
